/**
 * folderPaywall 渲染器 · 把 FolderPaywallModalData 渲染成开通弹窗插槽（PRD §3.4）。
 * 覆盖三种来源：batch（AI 批量）、single（单个创建）、resident（常驻入口）。
 * 注册副作用在模块顶层执行；各卡点处静态 import 本文件的 folderPaywallCustomContent
 * → 首次加载即注册。红数字样式对齐 Figma 14:5633（#ea4d3d Semibold）；
 * 常驻入口副标题按 PRD 用纯文本，不红字高亮。
 * tab 小字配额文案来自 data.tabNotes（接口4 tiers 驱动，缺失已在数据层回落硬编码）。
 */
import React from 'react'
import { registerPaymentContent } from '../VipPaymentModal/contentRegistry'
import type { FolderPaywallModalData } from './paywallModalData'

/** 红色高亮内联样式（文件夹侧自管内容样式，不依赖弹窗 class） */
const EM: React.CSSProperties = { color: '#ea4d3d', fontWeight: 600 }

export const FOLDER_PAYWALL_VARIANT = 'folderPaywall'

/**
 * tab 小字（item2 设计稿）：各档「{手动分类|AI 批量分类} · 至多 {配额}」，
 * 企业级为「AI 批量分类 · 更多文件夹」。配额数字来自 data.tabNotes（接口4 tiers 驱动）。
 * 各档配额（数字 + 个）统一红字高亮（d.tabNotes.X 形如「20个」整体标红）；
 * 企业级为「AI 批量分类 · 更多文件夹」无数字，不标红。灰字部分由弹窗 .tab-note 管。
 */
function renderTabNotes(
  d: FolderPaywallModalData
): Record<'basic' | 'advanced' | 'super' | 'enterprise', React.ReactNode> {
  return {
    basic: (
      <>
        手动分类 · 至多 <span style={EM}>{d.tabNotes.basic}</span>
      </>
    ),
    advanced: (
      <>
        AI 批量分类 · 至多 <span style={EM}>{d.tabNotes.advanced}</span>
      </>
    ),
    super: (
      <>
        AI 批量分类 · 至多 <span style={EM}>{d.tabNotes.super}</span>
      </>
    ),
    enterprise: <>AI 批量分类 · 更多文件夹</>
  }
}

/** 标题 · 按来源 + 会员/超额状态（PRD §3.4） */
function renderTitle(d: FolderPaywallModalData): string {
  if (d.kind === 'resident') {
    if (d.exceeded) return '文件夹个数已达上限'
    return d.isNovip ? '开通会员，解锁更多文件夹' : '升级会员，解锁更多文件夹'
  }
  if (d.kind === 'single') return '文件夹个数已达上限'
  // batch
  // item1（商业化）：AI 批量已是高级会员功能 → 非会员标题改「开通高级会员」；
  // 会员超额那条保持「升级会员」（高级会员超额其实要升超级，写死「高级」会错）
  return d.isNovip
    ? '开通高级会员，畅享 AI 批量分类特权'
    : '升级会员，畅享 AI 批量分类特权'
}

/** 副标题 · batch/single 高亮预计用量；resident 按 PRD 纯文本（不红字） */
function renderSubtitle(d: FolderPaywallModalData): React.ReactNode {
  if (d.kind === 'resident') {
    return d.isNovip
      ? `当前拥有 ${d.used}个 文件夹`
      : `${d.tierName}至多拥有 ${d.cap}个 文件夹，当前拥有 ${d.used}个`
  }
  // batch & single：本次新增后将拥有 {预计用量}（红）
  return d.isNovip ? (
    <>
      本次新增后将拥有 <span style={EM}>{d.newTotal}个</span> 文件夹
    </>
  ) : (
    <>
      {d.tierName}至多拥有 {d.cap}个 文件夹，本次新增后将拥有{' '}
      <span style={EM}>{d.newTotal}个</span>
    </>
  )
}

registerPaymentContent(FOLDER_PAYWALL_VARIANT, (d: FolderPaywallModalData) => ({
  title: renderTitle(d),
  subtitle: renderSubtitle(d),
  tabNotes: renderTabNotes(d)
}))

/** 拼 store 的 customContent（import 本函数即触发上面的注册副作用） */
export function folderPaywallCustomContent(d: FolderPaywallModalData) {
  return { variant: FOLDER_PAYWALL_VARIANT, data: d }
}
